/**
 * Changelog Modal Component
 * Shows version history when clicking on version number
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Bug, Zap, Calendar, ChevronRight } from 'lucide-react';
import axios from 'axios';

import { API } from '../config/api';

// Type icons and colors
const typeConfig = {
  feature: {
    icon: Sparkles,
    color: 'text-emerald-600 bg-emerald-100',
    label: 'Nouveauté'
  },
  fix: {
    icon: Bug,
    color: 'text-orange-600 bg-orange-100',
    label: 'Correction'
  },
  improvement: {
    icon: Zap,
    color: 'text-blue-600 bg-blue-100',
    label: 'Amélioration'
  }
};

export const ChangelogModal = ({ isOpen, onClose }) => {
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVersion, setExpandedVersion] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchChangelog();
    }
  }, [isOpen]);

  const fetchChangelog = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/changelog/`);
      setChangelog(response.data);
      // Expand the latest version by default
      if (response.data.length > 0) {
        setExpandedVersion(response.data[0].version);
      }
    } catch (error) {
      console.error('Failed to fetch changelog:', error);
      // Fallback data
      setChangelog([
        {
          version: 'V1.3.0',
          date: '2026-02-15',
          title: 'Prononciation Audio',
          type: 'feature',
          changes: [
            'Bouton de prononciation pour les termes japonais',
            'Choix de voix masculine/féminine',
            'Préférences vocales dans le compte'
          ]
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0F172A]">
                Historique des versions
              </h2>
              <p className="text-sm text-[#0F172A]/60 mt-1">
                Découvrez les nouveautés de Nishikigoi.fr
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-[#0F172A]" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(80vh-140px)]">
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="w-8 h-8 border-3 border-[#D32F2F] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {changelog.map((entry, index) => {
                  const config = typeConfig[entry.type] || typeConfig.feature;
                  const Icon = config.icon;
                  const isExpanded = expandedVersion === entry.version;
                  const isLatest = index === 0;

                  return (
                    <div key={entry.version} className="group">
                      <button
                        onClick={() => setExpandedVersion(isExpanded ? null : entry.version)}
                        className="w-full p-4 flex items-start gap-4 hover:bg-stone-50 transition-colors text-left"
                      >
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${config.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-[#D32F2F]">
                              {entry.version}
                            </span>
                            {isLatest && (
                              <span className="text-xs font-medium px-2 py-0.5 bg-[#D32F2F] text-white rounded-full">
                                Actuelle
                              </span>
                            )}
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.color}`}>
                              {config.label}
                            </span>
                          </div>
                          <h3 className="font-medium text-[#0F172A] mt-1">{entry.title}</h3>
                          <p className="text-xs text-[#0F172A]/50 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(entry.date)}
                          </p>
                        </div>

                        {/* Expand indicator */}
                        <ChevronRight 
                          className={`w-5 h-5 text-[#0F172A]/30 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`} 
                        />
                      </button>

                      {/* Expanded changes */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4 pl-18 ml-14">
                              <ul className="space-y-2">
                                {entry.changes.map((change, i) => (
                                  <li 
                                    key={i}
                                    className="flex items-start gap-2 text-sm text-[#0F172A]/70"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#D32F2F] mt-2 shrink-0" />
                                    {change}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-stone-100 bg-stone-50 text-center">
            <p className="text-xs text-[#0F172A]/50">
              Merci de suivre l'évolution de Nishikigoi.fr !
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ChangelogModal;
